import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { TabelaIbptService } from '../../../../shared/services/apis/motor-fiscal/tabela-ibpt.service';
import { SalvarDestinatarioIbptRequest } from '../../../../shared/services/requests/motor-fiscal/salvar-destinatario-ibpt.request';
import { ReturnStruct } from '../../../../shared/services/structs/return.struct';

export interface DestinatarioIbptDialogData {
  listaEmail: string[];
  isAvisarRetorno: boolean;
}

/**
 * Diálogo "Destinatários do alerta". A lista só é gravada no "Salvar"; "Cancelar" descarta as alterações.
 * Fecha com true só quando a API gravou.
 */
@Component({
  selector: 'app-destinatario-ibpt-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './destinatario-ibpt-dialog.component.html',
  styleUrl: './destinatario-ibpt-dialog.component.css',
})
export class DestinatarioIbptDialogComponent {
  public emailControl: FormControl<string | null> = new FormControl('', [Validators.email]);
  public erroEmail: string | null = null;
  public listaEmail: string[];
  public isAvisarRetorno: boolean;
  public isSalvando: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DestinatarioIbptDialogData,
    private dialogRef: MatDialogRef<DestinatarioIbptDialogComponent, boolean>,
    private tabelaIbptService: TabelaIbptService,
    private alertService: AlertService
  ) {
    // Cópias: o card só muda depois de salvar.
    this.listaEmail = [...(data?.listaEmail ?? [])];
    this.isAvisarRetorno = data?.isAvisarRetorno ?? false;
  }

  public adicionar(): void {
    const email = (this.emailControl.value ?? '').trim().toLowerCase();
    if (!email) {
      return;
    }

    if (this.emailControl.invalid || !email.includes('.')) {
      this.erroEmail = 'Informe um e-mail válido.';
      return;
    }

    if (this.listaEmail.some(e => e.toLowerCase() === email)) {
      this.erroEmail = 'Este e-mail já está na lista.';
      return;
    }

    this.listaEmail.push(email);
    this.emailControl.setValue('');
    this.erroEmail = null;
  }

  public remover(email: string): void {
    this.listaEmail = this.listaEmail.filter(e => e !== email);
  }

  public cancelar(): void {
    this.dialogRef.close(false);
  }

  public salvar(): void {
    if (this.listaEmail.length === 0 || this.isSalvando) {
      return;
    }

    const request: SalvarDestinatarioIbptRequest = {
      listaEmail: this.listaEmail,
      isAvisarRetorno: this.isAvisarRetorno,
    };

    this.isSalvando = true;
    this.dialogRef.disableClose = true;
    this.tabelaIbptService.salvarDestinatario(request).subscribe({
      next: (returnStruct: ReturnStruct) => {
        this.isSalvando = false;
        this.dialogRef.disableClose = false;
        if (returnStruct.isError) {
          this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
          return;
        }

        this.dialogRef.close(true);
      },
      error: (error: any) => {
        this.isSalvando = false;
        this.dialogRef.disableClose = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }
}
